import {Controller} from '@hotwired/stimulus';

const ARROWS = {
  horizontal: {previous: 'ArrowLeft', next: 'ArrowRight'},
  vertical: {previous: 'ArrowUp', next: 'ArrowDown'},
};

/**
 * Onglets de panneau (tabs.md › panel-tabs), posé sur un `role="tablist"` dont les onglets s'ouvrent par
 * le balisage Bootstrap (`data-bs-toggle="tab"`) : seul l'onglet actif est dans l'ordre de tabulation, et
 * les flèches, Début et Fin (`panel-tabs#navigate` sur `keydown`) activent l'onglet atteint. Les flèches
 * suivent `aria-orientation` : gauche et droite par défaut, haut et bas sur un menu de sections vertical.
 * Un onglet désactivé est sauté.
 */
export default class extends Controller {
  connect() {
    this.sync = this.sync.bind(this);
    this.element.addEventListener('shown.bs.tab', this.sync);
    this.sync();
  }

  disconnect() {
    this.element.removeEventListener('shown.bs.tab', this.sync);
  }

  navigate(event) {
    const tabs = this.tabs.filter((tab) => !tab.disabled);
    const current = tabs.indexOf(event.target.closest('[role="tab"]'));
    const {previous, next} = ARROWS[this.orientation];

    const target = {
      [previous]: (current - 1 + tabs.length) % tabs.length,
      [next]: (current + 1) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    }[event.key];

    if (current === -1 || target === undefined) {
      return;
    }

    event.preventDefault();

    tabs[target].focus();
    tabs[target].click();
  }

  sync() {
    const tabs = this.tabs;
    const enabled = tabs.filter((tab) => !tab.disabled);
    // aucun onglet sélectionné, ou un sélectionné désactivé : le premier onglet utilisable garde la liste au clavier
    const tabbable = enabled.find((tab) => tab.classList.contains('active')) ?? enabled[0];

    tabs.forEach((tab) => {
      tab.tabIndex = tab === tabbable ? 0 : -1;
      tab.setAttribute('aria-selected', String(tab.classList.contains('active')));
    });
  }

  get orientation() {
    return this.element.getAttribute('aria-orientation') === 'vertical' ? 'vertical' : 'horizontal';
  }

  get tabs() {
    return [...this.element.querySelectorAll('[role="tab"]')];
  }
}
